"use client";

import { useEffect, useState } from "react";
import { HardDrive, Info } from "lucide-react";
import { useLanguage } from "@/components/LanguageProvider";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import { unwrapApiData } from "@/lib/apiResponse";
import { useAppResumeRevision } from "@/lib/useAppResumeRevision";

const EMPTY_STORAGE_VALUE = "—";

export function StorageDashboard() {
  const { t } = useLanguage();
  const resumeRevision = useAppResumeRevision();
  const [storage, setStorage] = useState<{
    planName: string;
    used: number;
    limit: number | null;
  } | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void fetchWithAuth(
      `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000"}/api/pricing/entitlements/me?scope=user`,
      { signal: controller.signal },
    )
      .then(async (response) => {
        if (!response.ok) throw new Error(String(response.status));
        const data = unwrapApiData<Record<string, unknown>>(
          await response.json(),
        );
        const plan =
          data.plan && typeof data.plan === "object"
            ? (data.plan as Record<string, unknown>)
            : {};
        const usage =
          data.usage && typeof data.usage === "object"
            ? (data.usage as Record<string, unknown>)
            : {};
        const rawLimit = usage.storageLimitBytes;
        if (!controller.signal.aborted) {
          setStorage({
            planName: String(plan.planName ?? "Free"),
            used: Number(usage.storageUsedBytes ?? 0),
            limit: rawLimit == null ? null : Number(rawLimit),
          });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setStorage({ planName: "—", used: 0, limit: 0 });
      });
    return () => controller.abort();
  }, [resumeRevision]);

  const usedGb = storage ? storage.used / 1073741824 : null;
  const limitGb = storage?.limit == null ? null : storage.limit / 1073741824;
  const remainingGb =
    storage && storage.limit != null
      ? Math.max(0, storage.limit - storage.used) / 1073741824
      : null;
  const percent =
    storage?.limit && storage.limit > 0
      ? Math.min(100, Math.round((storage.used / storage.limit) * 100))
      : 0;
  const display = (value: number | null) =>
    value === null
      ? storage
        ? "∞"
        : EMPTY_STORAGE_VALUE
      : value.toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <div>
      <div className="mb-4 border-b border-gray-200 pb-4 dark:border-[#2d2d30]">
        <h2 className="text-[16px] font-semibold tracking-tight text-gray-950 dark:text-gray-50">
          {t("settings.storage")}
        </h2>
        <p className="mt-1 text-[11.5px] leading-relaxed text-gray-500 dark:text-gray-400">
          {t("storage.desc")}
        </p>
      </div>

      <section
        aria-labelledby="storage-plan-title"
        className="rounded-xl bg-gray-100/80 px-4 py-4 dark:bg-white/[0.06]"
      >
        <div className="flex items-start gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-gray-600 shadow-sm dark:bg-[#2a2a2d] dark:text-gray-300">
            <HardDrive size={17} strokeWidth={1.8} />
          </span>
          <div className="min-w-0 flex-1">
            <p
              id="storage-plan-title"
              className="text-[10.5px] font-medium text-gray-500 dark:text-gray-400"
            >
              {t("storage.current_plan")}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <p className="text-[13px] font-semibold text-gray-950 dark:text-gray-50">
                {storage?.planName ?? t("storage.connection_pending")}
              </p>
              {storage?.limit == null && storage && (
                <span className="rounded-md bg-white px-2 py-0.5 text-[9px] font-semibold text-gray-500 dark:bg-white/10 dark:text-gray-400">
                  무제한
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-5">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-[10.5px] font-medium text-gray-500 dark:text-gray-400">
                {t("storage.used")}
              </p>
              <p className="mt-0.5 text-[22px] font-semibold tracking-tight text-gray-950 tabular-nums dark:text-gray-50">
                {display(usedGb)}
                <span className="ml-1 text-[11px] font-medium text-gray-400 dark:text-gray-500">
                  GB
                </span>
              </p>
            </div>
            <p className="pb-1 text-[10.5px] font-medium text-gray-400 tabular-nums dark:text-gray-500">
              {display(usedGb)} GB / {display(limitGb)} GB
            </p>
          </div>
          <div
            role="progressbar"
            aria-label={t("storage.used")}
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
            className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-white/10"
          >
            <div
              className="h-full rounded-full bg-seexr-solid transition-[width]"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>
      </section>

      <dl className="mt-4 grid grid-cols-3 divide-x divide-gray-200 dark:divide-[#2d2d30]">
        <StorageMetric label={t("storage.used")} value={display(usedGb)} />
        <StorageMetric
          label={t("storage.allocated")}
          value={display(limitGb)}
        />
        <StorageMetric
          label={t("storage.remaining")}
          value={display(remainingGb)}
        />
      </dl>

      <div className="mt-5 flex items-start gap-2.5 rounded-lg bg-gray-50 px-3 py-3 text-gray-500 dark:bg-white/[0.035] dark:text-gray-400">
        <Info size={15} strokeWidth={1.8} className="mt-0.5 shrink-0" />
        <div>
          <p className="text-[10.5px] font-medium leading-relaxed">
            {t("storage.plan_note")}
          </p>
          <p className="mt-0.5 text-[10px] leading-relaxed text-gray-400 dark:text-gray-500">
            개인 앱 업로드는 내 한도에, Platform 팀 업로드는 팀 소유자 한도에
            합산됩니다.
          </p>
        </div>
      </div>
    </div>
  );
}

function StorageMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 px-3 first:pl-0 last:pr-0">
      <dt className="truncate text-[9.5px] font-medium text-gray-400 dark:text-gray-500">
        {label}
      </dt>
      <dd className="mt-1 text-[13px] font-semibold text-gray-900 tabular-nums dark:text-gray-100">
        {value}
        <span className="ml-1 text-[9.5px] font-medium text-gray-400 dark:text-gray-500">
          GB
        </span>
      </dd>
    </div>
  );
}
